import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';

import {
  createProcessingJob,
  getProcessingJob,
  type ProcessingJob,
  type StepDescriptor,
} from 'api/processingJobsClient';
import type { StepColorMap } from 'types/stepRun';
import { ErrorMessage } from 'components/common/ErrorMessage';
import { parseJson } from 'utils/parseJson';
import { JobStatusCard } from './JobStatusCard';
import { FilePicker } from './FilePicker';
import Styled from './RunWorkflowModal.styled';

const {
  StyledRoot,
  StyledSectionLabel,
  StyledRequestTextarea,
  StyledRunRow,
  StyledRunButton,
  StyledResetButton,
  StyledRequestErrorSlot,
} = Styled;

const TERMINAL_STATUSES: string[] = ['succeeded', 'failed', 'skipped'];
const MAX_POLLS = 900; // 15 minutes at 1s interval

export type { StepColorMap };

export interface RunnerState {
  requestJson: string;
  selectedFileName: string | null;
  selectedFileSize: number | null;
}

export const defaultRunnerState = (workflowId: string): RunnerState => ({
  requestJson: JSON.stringify(
    { workflowId, metadata: {}, definedInputs: {}, settings: {}, inputFileConfigurations: {} },
    null,
    2,
  ),
  selectedFileName: null,
  selectedFileSize: null,
});

interface OwnProps {
  workflowId: string;
  state: RunnerState;
  selectedFile: File | null;
  onStateChange: Dispatch<SetStateAction<RunnerState>>;
  onSelectedFileChange: (file: File | null) => void;
  onStepColorsChange: (colors: StepColorMap | null) => void;
  onIsRunningChange?: (isRunning: boolean) => void;
}

/** Inline panel for the right sidebar */
const RunWorkflowPanel = ({
  workflowId,
  state,
  selectedFile,
  onStateChange,
  onSelectedFileChange,
  onStepColorsChange,
  onIsRunningChange,
}: OwnProps) => {
  const pollCountRef = useRef(0);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);

  const [parseError, setParseError] = useState<string | null>(null);
  const [isRunning, setIsRunningInternal] = useState(false);
  const [job, setJob] = useState<ProcessingJob | null>(null);
  const [pollError, setPollError] = useState<string | null>(null);

  const { requestJson } = state;

  const setRequestJson = useCallback(
    (next: string) => {
      onStateChange((prev) => ({ ...prev, requestJson: next }));
    },
    [onStateChange],
  );

  const setIsRunning = useCallback(
    (v: boolean) => {
      setIsRunningInternal(v);
      onIsRunningChange?.(v);
    },
    [onIsRunningChange],
  );

  const applyStepColors = useCallback(
    (latestJob: ProcessingJob) => {
      const colors: StepColorMap = {};
      const completedSteps = latestJob.runtimeInfo?.completedSteps ?? [];
      completedSteps.forEach((desc: StepDescriptor) => {
        if (desc.stepId) {
          colors[desc.stepId] = desc.isSkipped ? 'skipped' : 'completed';
        }
      });
      if (latestJob.status.toLowerCase() === 'succeeded') {
        colors.Output = 'completed';
      }

      if (latestJob.status.toLowerCase() === 'failed' && latestJob.error?.target) {
        colors[latestJob.error.target] = 'failed';
      }
      onStepColorsChange(Object.keys(colors).length > 0 ? colors : null);
    },
    [onStepColorsChange],
  );

  const pollJob = useCallback(
    async (jobId: string) => {
      pollCountRef.current += 1;

      if (pollCountRef.current > MAX_POLLS) {
        setPollError('Polling timed out after 15 minutes.');
        setIsRunning(false);
        return;
      }

      try {
        const updated = await getProcessingJob(jobId, true);
        applyStepColors(updated);
        if (isMountedRef.current) {
          setJob(updated);
        }

        if (TERMINAL_STATUSES.includes(updated.status.toLowerCase())) {
          if (isMountedRef.current) setIsRunning(false);
          return;
        }

        pollTimerRef.current = setTimeout(() => void pollJob(jobId), 1000);
      } catch (err) {
        if (!isMountedRef.current) return;
        setPollError(err instanceof Error ? err.message : 'Polling failed');
        setIsRunning(false);
      }
    },
    [applyStepColors, setIsRunning],
  );

  const handleRun = useCallback(async () => {
    setParseError(null);
    setPollError(null);
    setJob(null);
    pollCountRef.current = 0;

    const parsed = parseJson<Record<string, unknown>>(requestJson);
    if (!parsed.ok || parsed.value === null) {
      setParseError(parsed.error ?? 'Invalid JSON');
      return;
    }

    setIsRunning(true);

    try {
      const request = {
        ...parsed.value,
        workflowId,
      };
      const created = await createProcessingJob(
        request as Parameters<typeof createProcessingJob>[0],
        selectedFile ?? undefined,
      );
      if (!isMountedRef.current) return;
      setJob(created);
      applyStepColors(created);

      if (!TERMINAL_STATUSES.includes(created.status.toLowerCase())) {
        pollTimerRef.current = setTimeout(() => void pollJob(created.processingJobId), 1000);
      } else {
        setIsRunning(false);
      }
    } catch (err) {
      if (!isMountedRef.current) return;
      setPollError(err instanceof Error ? err.message : 'Failed to create job');
      setIsRunning(false);
    }
  }, [requestJson, workflowId, selectedFile, applyStepColors, pollJob, setIsRunning]);

  const handleClearColors = useCallback(() => {
    onStepColorsChange(null);
  }, [onStepColorsChange]);

  const handleRequestChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setRequestJson(e.target.value);
      setParseError(null);
    },
    [setRequestJson],
  );

  const handleFileChange = useCallback(
    (file: File | null) => {
      onSelectedFileChange(file);
      onStateChange((prev) => ({
        ...prev,
        selectedFileName: file?.name ?? null,
        selectedFileSize: file?.size ?? null,
      }));
    },
    [onSelectedFileChange, onStateChange],
  );

  const handleRunClick = useCallback(() => {
    void handleRun();
  }, [handleRun]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return (
    <StyledRoot>
      {/* Request JSON */}
      <section>
        <StyledSectionLabel>Request JSON</StyledSectionLabel>
        <StyledRequestTextarea
          value={requestJson}
          onChange={handleRequestChange}
          rows={8}
          spellCheck={false}
          aria-label='Request JSON'
          $hasError={parseError !== null}
        />
        {parseError && (
          <StyledRequestErrorSlot>
            <ErrorMessage monospace>{parseError}</ErrorMessage>
          </StyledRequestErrorSlot>
        )}
      </section>

      {/* File attachment */}
      <section>
        <StyledSectionLabel>File (optional)</StyledSectionLabel>
        <FilePicker
          selectedFile={selectedFile}
          previousFileName={state.selectedFileName}
          onFileChange={handleFileChange}
        />
      </section>

      {/* Run + reset-colors */}
      <StyledRunRow>
        <StyledRunButton
          type='button'
          onClick={handleRunClick}
          className='btn-sm'
          disabled={isRunning}
          title='Create a new processing job for this workflow'
        >
          {isRunning ? 'Running…' : 'Run'}
        </StyledRunButton>
        <StyledResetButton
          type='button'
          onClick={handleClearColors}
          className='btn-sm btn-secondary'
          title='Remove green/red highlights from the canvas'
        >
          Reset colors
        </StyledResetButton>
      </StyledRunRow>

      {/* Job Status */}
      {job && <JobStatusCard job={job} isRunning={isRunning} onClearColors={handleClearColors} />}

      {pollError && <ErrorMessage>{pollError}</ErrorMessage>}
    </StyledRoot>
  );
};

const MemoRunWorkflowPanel = React.memo(RunWorkflowPanel);

export { MemoRunWorkflowPanel as RunWorkflowPanel };
export default MemoRunWorkflowPanel;
